/**
 * Twelve choices, no octaves.
 *
 * A learner naming the 7th of a chord thinks "B", not "B4". The register is a
 * separate decision the app makes for them, so it is kept out of the model the
 * picker exposes — `Pitch` still carries an octave internally, it just never
 * surfaces here.
 */

import { PITCH_CLASSES, pitchClassName, type AccidentalPreference } from '../theory/voicing';

export interface PitchClassPickerProps {
  value: number;
  onChange: (pitchClass: number) => void;
  preference: AccidentalPreference;
  onPreferenceChange: (preference: AccidentalPreference) => void;
  label: string;
}

export function PitchClassPicker({
  value,
  onChange,
  preference,
  onPreferenceChange,
  label,
}: PitchClassPickerProps) {
  return (
    <div className="pc-picker">
      <div className="pc-head">
        <span className="pc-label">{label}</span>
        <div className="pc-pref" role="group" aria-label="Enharmonic spelling preference">
          {(['sharp', 'flat'] as const).map((option) => (
            <button
              key={option}
              type="button"
              className="mm-note-chip"
              aria-pressed={preference === option}
              onClick={() => onPreferenceChange(option)}
              title={
                option === 'sharp'
                  ? 'Prefer sharp spellings (C♯, F♯)'
                  : 'Prefer flat spellings (D♭, G♭)'
              }
            >
              {option === 'sharp' ? '♯' : '♭'}
            </button>
          ))}
        </div>
      </div>

      <div className="pc-grid" role="radiogroup" aria-label={label}>
        {PITCH_CLASSES.map((pc) => {
          const name = pitchClassName(pc, preference);
          const isAltered = name.length > 1;
          return (
            <button
              key={pc}
              type="button"
              role="radio"
              aria-checked={pc === value}
              className={`pc-key ${pc === value ? 'is-active' : ''} ${
                isAltered ? 'is-altered' : 'is-natural'
              }`}
              onClick={() => onChange(pc)}
            >
              {name}
            </button>
          );
        })}
      </div>
    </div>
  );
}
